import { useState, useEffect } from 'react';
import { childConnectionService } from '@/services/childConnectionService';

export interface ConnectedChild {
  id: string;
  child_id: string;
  parent_id: string;
  connection_method: string;
  connected_at: string;
  child: {
    id: string;
    username: string;
    role: string;
    age_group: string;
  };
}

export interface ChildTeam {
  team_id: number;
  team_name: string;
  sport_id: number;
  slug: string;
  logo_url?: string;
  age_group: string;
  is_child_team: boolean;
  child_id: string;
}

export const useChildConnections = (parentId: string, userRole: string) => {
  const [connectedChildren, setConnectedChildren] = useState<ConnectedChild[]>([]);
  const [childTeams, setChildTeams] = useState<ChildTeam[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Only load for parent users
  const isParent = userRole === 'parent';

  useEffect(() => {
    if (isParent && parentId) {
      loadConnections();
    } else {
      setLoading(false);
    }
  }, [parentId, isParent]);

  const loadConnections = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load connected children
      const children = await childConnectionService.getConnectedChildren(parentId);
      setConnectedChildren(children);

      // Load teams for each connected child
      const allChildTeams: ChildTeam[] = [];
      for (const connection of children) {
        if (connection.child?.id) {
          const teams = await childConnectionService.getChildTeams(connection.child.id);
          allChildTeams.push(...(teams as ChildTeam[]));
        }
      }
      setChildTeams(allChildTeams);

    } catch (err) {
      console.error('Error loading child connections:', err);
      setError('Failed to load child connections');
    } finally {
      setLoading(false);
    }
  };

  const refreshConnections = () => {
    if (isParent && parentId) {
      console.log('🔄 HOOK - Force refreshing connections for parent:', parentId);
      loadConnections();
    }
  };

  return {
    connectedChildren,
    childTeams,
    loading,
    error,
    refreshConnections,
    hasConnections: connectedChildren.length > 0,
    totalChildren: connectedChildren.length,
    totalChildTeams: childTeams.length
  };
};

export const useChildConnectionCode = (userId: string, userAgeGroup: string) => {
  const [connectionCode, setConnectionCode] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Only load for under 13 users
  const normalizedAgeGroup = userAgeGroup && ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12', '13', '13-and-under'].includes(userAgeGroup) 
    ? '13-and-under' 
    : userAgeGroup;
  
  const isUnder13 = normalizedAgeGroup === '13-and-under';

  useEffect(() => {
    if (isUnder13 && userId) {
      loadConnectionCode();
    } else {
      setLoading(false);
    }
  }, [userId, isUnder13]);

  const loadConnectionCode = async () => {
    try {
      setLoading(true);
      setError(null);

      // Use the new method that prevents unnecessary regeneration
      const code = await childConnectionService.getOrCreateChildCode(userId);
      if (code) {
        setConnectionCode(code);
      } else {
        setError('Failed to get connection code');
      }
    } catch (err) {
      console.error('Error loading connection code:', err);
      setError('Failed to load connection code');
    } finally {
      setLoading(false);
    }
  };

  const regenerateCode = async (): Promise<boolean> => {
    try {
      const newCode = await childConnectionService.generateNewConnectionCode(userId);
      if (newCode) {
        setConnectionCode(newCode);
        return true;
      }
      return false;
    } catch (err) {
      console.error('Error regenerating code:', err);
      return false;
    }
  };

  return {
    connectionCode,
    loading,
    error,
    isUnder13,
    regenerateCode,
    refreshCode: loadConnectionCode
  };
};